import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  useMediaQuery,
  Link,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  InputAdornment,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  LocationOn,
  Search,
  HourglassTop,
  WorkspacePremium,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlaceService } from '../../services/place/PlaceService';
import Theme from '../../theme/Theme';
import styles from './Register.module.css';
import { LoginService } from '../../services/LoginService';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import PaymentFullScreenModal from '../../components/template/PaymentFullScreenModalProps';
import { PaymentService } from '../../services/payment/PaymentService';
import PlansModal from '../../components/template/PlansModal';
import { Plan, plans } from '../../interfaces/Plan';
import { PlaceOrderService } from '../../services/placeOrder/PlaceOrderService';

export default function PlaceList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  if (!LoginService.isAuthenticated()) {
    LoginService.logout();
    navigate('/login');
  }

  const [places, setPlaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlaceId, setSelectedPlaceId] = useState<number>();

  const [openModal, setOpenModal] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [loadingPayment, setLoadingPayment] = useState(false);

  const isMobile = useMediaQuery(Theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(Theme.breakpoints.down('md'));
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [search, setSearch] = useState('');
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [openBackpackModal, setOpenBackpackModal] = useState(false);

  const normalize = (s: string) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  const filteredPlaces = places.filter((p) => {
    if (!search) return true;
    const q = normalize(search);
    return (
      normalize(p.fantasyName).includes(q) ||
      normalize(`${p.address} ${p.districtName} ${p.cityName}`).includes(q)
    );
  });

  const paginatedData = filteredPlaces.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const pageCount = Math.ceil(filteredPlaces.length / itemsPerPage);

  const loadPlaces = async () => {
    try {
      const response = await PlaceService.findAll();
      setPlaces(response || []);
    } catch (err: any) {
      console.error('Erro ao buscar espaços', +err?.response?.data?.detail);
    } finally {
      setLoading(false);
    }
  };

  const newPayment = async (placeId: number, plan: string) => {
    setSelectedPlaceId(placeId);
    setSelectedPlan(plans.find((p) => p.type === plan) || null);
  };

  const checkPaymentStatus = async (placeId: number) => {
    try {
      const response = await PaymentService.status(placeId);

      if (Array.isArray(response) && response.length > 0) {
        setLoadingPayment(true);
        setPayments(response as any[]);
        setOpenModal(true);
      } else {
        setSelectedPlaceId(placeId);
      }
    } catch (err: any) {
      console.error('Erro ao verificar status de pagamento', err?.response?.data?.detail);
      setSelectedPlaceId(placeId);
      return null;
    } finally {
      setLoadingPayment(false);
    }
  };

  useEffect(() => {
    if (selectedPlaceId) setOpenPaymentModal(true);
  }, [selectedPlaceId]);

  useEffect(() => {
    loadPlaces();
  }, []);

  useEffect(() => {
    setPendingLoading(true);
    PlaceOrderService.getPendingCount()
      .then((response) => {
        setPendingCount(response.data ?? 0);
      })
      .catch(() => {
        setPendingCount(0);
      })
      .finally(() => setPendingLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, itemsPerPage]);

  useEffect(() => {
    if (searchParams.get('backpack')) {
      setOpenBackpackModal(true);
    }
  }, [searchParams]);

  const handleCloseBackpackModal = () => {
    setOpenBackpackModal(false);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('backpack');
    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleRemove = async (place: any) => {
    const confirm = prompt(
      `Atenção! Esta ação é irreversível e irá deletar todos os dados relacionados. Deseja realmente remover este espaço? Digite o nome do espaço para Confirmar **${place.fantasyName.trim()}**`,
    );

    if (!confirm || confirm !== place.fantasyName.trim()) return;

    try {
      await PlaceService.remove(place.id);
      setPlaces((prev) => prev.filter((p) => p.id !== place.id));
    } catch (err: any) {
      alert('Erro ao remover espaço: ' + err?.response?.data?.detail);
      window.location.reload();
    }
  };

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const [openPaymentModal, setOpenPaymentModal] = useState(false);

  const [openPlanModal, setOpenPlanModal] = useState(false);
  const [planModalMode, setPlanModalMode] = useState<'create' | 'edit'>('create');
  const [planModalPlaceId, setPlanModalPlaceId] = useState<number | undefined>(undefined);
  const [planModalCurrentType, setPlanModalCurrentType] = useState<string | undefined>(undefined);
  const handleCLosePlanModal = () => {
    setOpenPlanModal(false);
    setPlanModalMode('create');
    setPlanModalPlaceId(undefined);
    setPlanModalCurrentType(undefined);
  };

  const openCreatePlanFlow = () => {
    setPlanModalMode('create');
    setPlanModalPlaceId(undefined);
    setOpenPlanModal(true);
  };

  const openEditPlanFlow = (placeId: number, currentType?: string) => {
    setPlanModalMode('edit');
    setPlanModalPlaceId(placeId);
    setPlanModalCurrentType(currentType);
    setOpenPlanModal(true);
  };

  const isPreRegistered = (place: any) => place?.type === 'PRE_REGISTRED';
  const isFreePlan = (place: any) => place?.type === 'FREE';

  const handleEditPlace = (place: any) => {
    if (isPreRegistered(place)) {
      openEditPlanFlow(place.id);
      return;
    }
    navigate(`/editar/espaco/${place.id}`);
  };

  return (
    <Box className={styles.container} display="flex" justifyContent="center" px={isMobile ? 0 : 3}>
      <Paper
        sx={{
          p: isMobile ? 2 : 5,
          width: '100%',
          maxWidth: 1200,
          m: isMobile ? 0 : 5,
        }}
      >
        <Box mb={3}>
          <Typography variant="h5" fontWeight="bold" mb={2}>
            Painel de Controle
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Solicitações pendentes
                </Typography>
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => navigate('/solicitacoes-abertas')}
                  sx={{ fontSize: 12, fontWeight: 600 }}
                >
                  Ver todas
                </Link>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {pendingLoading ? '...' : pendingCount ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Aguardando resposta dos espaços
              </Typography>
            </Paper>
          </Box>
        </Box>
        {/* Header */}
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'center'}
          gap={2}
          mb={3}
        >
          <Typography variant="h5" fontWeight="bold">
            Espaços
          </Typography>
          <Box display="flex" gap={2} alignItems={isMobile ? 'stretch' : 'center'} flexWrap="wrap">
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou endereço"
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl variant="standard" sx={{ minWidth: 100 }}>
              <InputLabel id="items-per-page-label">Itens</InputLabel>
              <Select
                labelId="items-per-page-label"
                value={itemsPerPage}
                onChange={(e) => {
                  setPage(1);
                  setItemsPerPage(Number(e.target.value));
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button
              size={isMobile ? 'medium' : 'large'}
              color="warning"
              variant="contained"
              endIcon={<Add />}
              onClick={openCreatePlanFlow}
            >
              Novo Espaço
            </Button>
            <PlansModal
              open={openPlanModal}
              onClose={handleCLosePlanModal}
              mode={planModalMode}
              placeId={planModalPlaceId}
              currentPlanType={planModalCurrentType}
            />
          </Box>
        </Box>

        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : paginatedData.length ? (
            isMobile ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {paginatedData.map((place) => (
                  <Paper key={place.id} sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={2}
                    >
                      <Box>
                        <Link href={`/espaco/${place.id}`} underline="none" color="warning">
                          <Typography variant="subtitle1" fontWeight={700}>
                            {place.fantasyName}
                          </Typography>
                        </Link>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          {place.address}, {place.districtName} - {place.cityName}
                        </Typography>
                      </Box>
                      <Chip
                        icon={isPreRegistered(place) ? <HourglassTop /> : undefined}
                        label={
                          isPreRegistered(place)
                            ? 'Pré-cadastro em andamento'
                            : place.status === 'ACTIVE'
                            ? 'Ativo'
                            : 'Pendente'
                        }
                        color={
                          isPreRegistered(place)
                            ? 'info'
                            : place.status === 'ACTIVE'
                            ? 'success'
                            : 'warning'
                        }
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {isPreRegistered(place) && (
                      <Paper
                        variant="outlined"
                        sx={{
                          mt: 1.5,
                          p: 1.2,
                          borderColor: 'info.light',
                          backgroundColor: 'info.50',
                        }}
                      >
                        <Typography variant="caption" color="info.main" fontWeight={700}>
                          Processo pendente
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Selecione um plano para concluir o cadastro e liberar o espaço.
                        </Typography>
                      </Paper>
                    )}

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Saldo
                        </Typography>
                        {place.balance ? (
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={place.balance > 5 ? 'success.main' : 'warning.main'}
                          >
                            {Number(place.balance).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </Box>

                      <Box display="flex" gap={1}>
                        {isFreePlan(place) && (
                          <Tooltip title="Fazer upgrade de plano">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => openEditPlanFlow(place.id, 'FREE')}
                            >
                              <WorkspacePremium fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEditPlace(place)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            place.status !== 'ACTIVE' ? 'Pagamento Pendente' : 'Sem pendências'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              place.status !== 'ACTIVE' && checkPaymentStatus(place.id)
                            }
                          >
                            {place.status !== 'ACTIVE' ? (
                              <LocationOffIcon color="error" fontSize="small" />
                            ) : (
                              <LocationOn color="success" fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleRemove(place)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table size={isMobile ? 'small' : 'medium'} stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Localização</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((place) => (
                      <TableRow hover key={place.id}>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Link href={`/espaco/${place.id}`} underline="none" color="warning">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {place.fantasyName}
                            </Typography>
                          </Link>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 380 }}>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {place.address}, {place.districtName} - {place.cityName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {place.balance ? (
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={place.balance > 5 ? 'success.main' : 'warning.main'}
                            >
                              {Number(place.balance).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={isPreRegistered(place) ? <HourglassTop /> : undefined}
                            label={
                              isPreRegistered(place)
                                ? 'Pré-cadastro em andamento'
                                : place.status === 'ACTIVE'
                                ? 'Ativo'
                                : 'Pendente'
                            }
                            color={
                              isPreRegistered(place)
                                ? 'info'
                                : place.status === 'ACTIVE'
                                ? 'success'
                                : 'warning'
                            }
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                          {isPreRegistered(place) && (
                            <Typography
                              variant="caption"
                              color="info.main"
                              display="block"
                              mt={0.5}
                            >
                              Processo pendente: selecione um plano para concluir o cadastro.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isFreePlan(place) && (
                            <Tooltip title="Fazer upgrade de plano">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => openEditPlanFlow(place.id, 'FREE')}
                              >
                                <WorkspacePremium fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleEditPlace(place)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              place.status !== 'ACTIVE' ? 'Pagamento Pendente' : 'Sem pendências'
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                place.status !== 'ACTIVE' && checkPaymentStatus(place.id)
                              }
                            >
                              {place.status !== 'ACTIVE' ? (
                                <LocationOffIcon color="error" fontSize="small" />
                              ) : (
                                <LocationOn color="success" fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRemove(place)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }} variant="outlined">
              <Typography variant="body1" color="text.secondary">
                Nenhum espaço encontrado.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Paginação */}
        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChange}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        )}
      </Paper>

      {!!selectedPlaceId && (
        <PaymentFullScreenModal
          open={openPaymentModal}
          placeId={selectedPlaceId}
          selectedPlan={selectedPlan}
          onClose={() => {
            setOpenModal(false);
            setOpenPaymentModal(false);
            setSelectedPlaceId(0);
          }}
        />
      )}

      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
        <DialogTitle>Revisão de Cobranças</DialogTitle>
        <DialogContent dividers>
          {loadingPayment ? (
            <Typography variant="body2">Carregando...</Typography>
          ) : payments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma cobrança encontrada.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {payments.map((p) => (
                <Box
                  key={p.invoiceUrl}
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={isMobile ? 'column' : 'row'}
                  alignItems="center"
                  p={2}
                  borderRadius={2}
                  sx={{
                    border: '1px solid',
                    borderColor:
                      p.status === 'PAID'
                        ? 'success.light'
                        : p.status === 'EXPIRED'
                        ? 'error.light'
                        : 'warning.light',
                    backgroundColor:
                      p.status === 'PAID'
                        ? 'success.50'
                        : p.status === 'EXPIRED'
                        ? 'error.50'
                        : 'warning.50',
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color={
                        p.status === 'PAID'
                          ? 'success.main'
                          : p.status === 'EXPIRED'
                          ? 'error.main'
                          : 'warning.main'
                      }
                      fontWeight="bold"
                    >
                      {p.status === 'EXPIRED'
                        ? 'Vencida'
                        : p.status === 'COMPLETED' || p.status === 'PAID'
                        ? 'Paga'
                        : 'Pendente'}
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    gap={2}
                    flexDirection={isMobile ? 'column' : 'row'}
                    alignItems="center"
                  >
                    {!!p.invoiceUrl && (
                      <Button
                        href={p.invoiceUrl}
                        target="_blank"
                        size="small"
                        variant="outlined"
                        color={p.status === 'EXPIRED' ? 'error' : 'primary'}
                      >
                        Abrir Fatura
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      onClick={() => newPayment(p.placeId, p.planName)}
                    >
                      Nova Cobrança
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBackpackModal}
        onClose={handleCloseBackpackModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 3,
            px: 3,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fff7e6 0%, #ffe0b2 100%)',
          }}
        >
          <Typography variant="overline" sx={{ letterSpacing: 1.4, color: 'warning.dark' }}>
            Presente desbloqueado
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: 'text.primary' }}>
            Parabens, voce acabou de desbloquear sua mochila
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, backgroundColor: '#fffdf8' }}>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={2}>
            Use o codigo abaixo para acessar o seu presente.
          </Typography>
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'warning.light',
              background: 'linear-gradient(180deg, #fff8e1 0%, #fff3cd 100%)',
              px: 2,
              py: 2.5,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1.2 }}>
              SENHA DA MOCHILA
            </Typography>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                mt: 0.5,
                color: 'warning.dark',
                letterSpacing: 4,
              }}
            >
              {searchParams.get('backpack')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            onClick={handleCloseBackpackModal}
            variant="contained"
            color="warning"
            sx={{ minWidth: 140, borderRadius: 999 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
